// 채팅 별도 창의 **세션 창 쪽**(설계서 §9.6.6) — 창을 열고 닫고, 상태를 보내고, 사람의 동작을 받아 처리한다.
//
// 규칙:
//   - 창이 필요한 때 = 채팅을 쓸 수 있고 · 열려 있고 · 표시 방식이 `popup`(= 별도 창)일 때. 그 밖에는 닫는다.
//   - 창을 열지 못하면(브라우저 팝업 차단 등) **옆에 고정으로 되돌리고** 이유를 알린다 — 채팅이 사라지면 안 된다.
//   - 사람이 창을 X 로 닫으면(`bye`) 채팅을 "닫힘" 으로 돌린다. 🔴 우리가 닫은 경우(고정으로 돌리기·채팅 닫기)의
//     `bye` 는 무시한다 — 안 그러면 "고정으로" 를 눌렀는데 채팅이 통째로 닫힌다.
//   - 세션이 끝나면(화면이 사라지면) `end` 를 보내고 닫는다. 앱은 세션 창이 사라질 때 Rust 가 한 번 더 닫는다.

import { useCallback, useEffect, useRef, useState } from 'react';

import type { Chat } from './useChat';
import type { ChatMode } from './useChatLayout';
import type { HostLink } from './popupLink';
import { openChatWindow } from './popupLink';
import {
  base64ToBytes,
  blobUrlToDataUrl,
  newChatKey,
  toWire,
  type ChatViewState,
  type WinToHost,
} from './popupProtocol';

/** 창을 열지 못했을 때의 안내(옆에 고정으로 되돌린 뒤 패널에 보인다). */
export const POPUP_BLOCKED_TEXT = '대화 창을 따로 띄우지 못해 옆에 고정했습니다(브라우저가 팝업을 막았을 수 있습니다).';

export interface ChatWindowControl {
  /** 별도 창이 떠 있는가. */
  isOpen: boolean;
  /** 창을 앞으로 가져온다(도구막대 [채팅]을 다시 눌렀을 때). */
  focus(): void;
  /** 창을 못 띄워 고정으로 되돌렸을 때의 안내. 없으면 undefined. */
  fallback?: string;
  clearFallback(): void;
}

/**
 * @param chat     세션 창의 채팅(진실원)
 * @param mode     표시 방식 — `popup` 이면 별도 창
 * @param setMode  표시 방식 바꾸기(창에서 "고정으로" · 창을 못 띄웠을 때)
 * @param title    창 제목(기기 이름)
 * @param maxImage 상대가 받는 그림 상한(바이트) — 창이 첨부할 때 미리 거른다
 */
export function useChatWindow(
  chat: Chat,
  mode: ChatMode,
  setMode: (m: ChatMode) => void,
  title: string,
  maxImage: number,
): ChatWindowControl {
  const want = chat.available && chat.open && mode === 'popup';
  const keyRef = useRef<string>(newChatKey());
  const linkRef = useRef<HostLink | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const openingRef = useRef(false);
  /** 우리가 닫는 중 — 그 뒤에 오는 `bye` 는 사람이 닫은 것이 아니다. */
  const closingByUsRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [fallback, setFallback] = useState<string | undefined>(undefined);
  /** 이미 data: URL 로 바꾼 그림(id → URL). 창이 다시 떠도(hello) 다시 바꾸지 않는다. */
  const imagesRef = useRef(new Map<string, string>());

  // 처리기(한 번 등록)가 최신 값을 보게 한다.
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const wantRef = useRef(want);
  wantRef.current = want;
  const viewRef = useRef<ChatViewState | null>(null);

  const post = useCallback((msg: Parameters<HostLink['post']>[0]) => linkRef.current?.post(msg), []);

  /** 그림 대화를 data: URL 로 바꿔 보낸다(바꾼 것은 기억). */
  const pushImages = useCallback(
    (all: boolean) => {
      for (const e of chatRef.current.entries) {
        if (e.kind !== 'image' || !e.imageUrl) {
          continue;
        }
        const done = imagesRef.current.get(e.id);
        if (done) {
          if (all) {
            post({ t: 'image', id: e.id, dataUrl: done });
          }
          continue;
        }
        const id = e.id;
        void blobUrlToDataUrl(e.imageUrl)
          .then((dataUrl) => {
            imagesRef.current.set(id, dataUrl);
            post({ t: 'image', id, dataUrl });
          })
          .catch(() => undefined); // 그림 하나 못 바꿔도 대화는 계속 보인다(자리만 빈다)
      }
    },
    [post],
  );

  const pushAll = useCallback(() => {
    if (viewRef.current) {
      post({ t: 'state', s: { ...viewRef.current, draft: chatRef.current.draft } });
    }
    post({ t: 'entries', entries: chatRef.current.entries.map(toWire) });
    pushImages(true);
  }, [post, pushImages]);

  const detach = useCallback(() => {
    unsubRef.current?.();
    unsubRef.current = null;
    linkRef.current = null;
    setIsOpen(false);
  }, []);

  const handle = useCallback(
    (msg: WinToHost) => {
      const c = chatRef.current;
      switch (msg.t) {
        case 'hello':
          pushAll();
          break;
        case 'send':
          c.sendRaw(msg.text, msg.png ? base64ToBytes(msg.png) : undefined);
          break;
        case 'download':
          c.download();
          break;
        case 'draft':
          c.setDraft(msg.text);
          break;
        case 'dock':
          c.setDraft(msg.draft);
          setMode('dock'); // 창은 아래 효과가 닫는다(want=false)
          break;
        case 'close':
          c.setOpen(false);
          break;
        case 'clearError':
          c.clearError();
          break;
        case 'clearNotice':
          c.clearNotice();
          break;
        case 'bye':
          detach();
          if (closingByUsRef.current) {
            closingByUsRef.current = false;
          } else {
            c.setOpen(false); // 사람이 창을 X 로 닫았다 = 채팅을 닫았다
          }
          break;
      }
    },
    [detach, pushAll, setMode],
  );

  // 열기·닫기.
  useEffect(() => {
    if (want && !linkRef.current && !openingRef.current) {
      openingRef.current = true;
      // 상대의 글이 와서 저절로 열렸으면 창을 활성화하지 않는다(원격 화면에 치던 글자가 새지 않게 — `openedQuietly`).
      void openChatWindow(keyRef.current, title, chat.openedQuietly).then((link) => {
        openingRef.current = false;
        if (!link) {
          setMode('dock');
          setFallback(POPUP_BLOCKED_TEXT);
          return;
        }
        linkRef.current = link;
        unsubRef.current = link.onMessage(handle);
        setIsOpen(true);
        setFallback(undefined);
        // 열리는 사이에 원하는 상태가 바뀌었으면(고정으로·닫기) 곧바로 닫는다.
        if (!wantRef.current) {
          closingByUsRef.current = true;
          link.close();
        }
        // 창이 이미 떠 있던 경우(앞으로 가져오기만 했다)에는 hello 가 다시 오지 않는다 → 한 번 밀어 준다.
        pushAll();
      });
    } else if (!want && linkRef.current) {
      closingByUsRef.current = true;
      const link = linkRef.current;
      // 앱: Rust 가 창 소멸 때 bye 를 보낸다 → 처리기가 detach. 브라우저: 감시가 bye 를 만든다.
      link.close();
    }
    // 🔴 `isOpen` 도 본다 — 닫자마자 다시 열면(bye 가 오기 전) 옛 창이 아직 붙어 있어 열지 못한다. bye 로 떨어져 나가는
    //    순간 다시 봐야 새 창이 뜬다(2026-09-29 파일 창 실세션 시험에서 같은 틀의 결함 발견 — `useFileWindow`).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openedQuietly 는 여는 순간의 값만 쓴다(바뀌었다고 다시 열지 않는다)
  }, [want, isOpen, title, handle, pushAll, setMode]);

  // 세션 화면이 사라지면 창도 닫는다.
  useEffect(
    () => () => {
      const link = linkRef.current;
      if (link) {
        link.post({ t: 'end' });
        closingByUsRef.current = true;
        link.close();
      }
      unsubRef.current?.();
    },
    [],
  );

  // 상태 보내기 — 그리는 데 필요한 값이 바뀔 때마다.
  const view: ChatViewState = {
    title,
    available: chat.available,
    canSend: chat.canSend,
    blockedReason: chat.blockedReason,
    canSendImage: chat.canSendImage,
    maxImage,
    error: chat.error,
    notice: chat.notice,
    draft: chat.draft,
  };
  viewRef.current = view;
  const viewKey = JSON.stringify({ ...view, draft: undefined }); // 초안은 창이 주인이다 — 되돌려 보내면 타이핑과 싸운다
  useEffect(() => {
    if (isOpen && viewRef.current) {
      post({ t: 'state', s: viewRef.current });
    }
  }, [isOpen, viewKey, post]);

  // 대화 목록 보내기.
  useEffect(() => {
    if (isOpen) {
      post({ t: 'entries', entries: chat.entries.map(toWire) });
      pushImages(false);
    }
  }, [isOpen, chat.entries, post, pushImages]);

  return {
    isOpen,
    focus: useCallback(() => linkRef.current?.focus(), []),
    fallback,
    clearFallback: useCallback(() => setFallback(undefined), []),
  };
}
